import {
  DatasetDiff,
  ComponentDiff,
  AccessLevelDiff,
} from "admin/components/dataset-detail/types/dataset-diff";
import { Loader2 } from "lucide-react";
import { cn } from "~/lib/utils";

interface DiffViewerProps {
  diffData?: DatasetDiff;
  isLoading: boolean;
  error?: Error;
}

interface DiffBoxProps {
  title: string;
  diffs: (ComponentDiff | AccessLevelDiff)[];
  className?: string;
}

const DiffBox = ({ title, diffs, className }: DiffBoxProps) => (
  <div className="flex-1">
    <p className={cn("text-sm font-semibold mb-2", className)}>
      {title} ({diffs.length})
    </p>
    <div className="border border-border rounded-md bg-muted/30 p-3 max-h-96 overflow-y-auto">
      <pre className="font-mono text-xs m-0 whitespace-pre-wrap">
        {diffs.length === 0 ? "[]" : JSON.stringify(diffs, null, 2)}
      </pre>
    </div>
  </div>
);

const DiffViewer = ({ diffData, isLoading, error }: DiffViewerProps) => {
  if (isLoading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading diff data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 border border-destructive rounded-md bg-destructive/10">
        <p className="text-sm text-destructive">Failed to load diff data: {error.message}</p>
      </div>
    );
  }

  if (!diffData || diffData.diffs.length === 0) {
    return (
      <div className="p-3 border border-green-500 rounded-md bg-green-50">
        <p className="text-sm text-green-700">
          No differences found. Catalog and Access Controller are in sync.
        </p>
      </div>
    );
  }

  const removedDiffs = diffData.diffs.filter((d) => d.diff_type === "removed");
  const addedDiffs = diffData.diffs.filter((d) => d.diff_type === "added");

  return (
    <div className="flex gap-4">
      <DiffBox title="Removed" diffs={removedDiffs} className="text-destructive" />
      <DiffBox title="Added" diffs={addedDiffs} className="text-green-700" />
    </div>
  );
};

export default DiffViewer;
